import { getJson, sendJson } from '@/lib/api';

/**
 * What the standard client portal shows. An event on an enterprise portal
 * shows everything; every other event follows the Settings default unless
 * it says otherwise for itself.
 */

/** One feature the standard portal can, or will, show. */
export interface PortalFeatureDefinition {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly defaultOn: boolean;
  /** False while the standard portal cannot show it yet. */
  readonly available: boolean;
}

/** A partial answer per feature key. */
export type PortalFeatureFlags = Readonly<Record<string, boolean>>;

export interface PortalFeatureDefaults {
  readonly catalogue: readonly PortalFeatureDefinition[];
  readonly defaults: PortalFeatureFlags;
}

export interface EventPortalFeatures extends PortalFeatureDefaults {
  readonly overrides: PortalFeatureFlags;
  readonly resolved: PortalFeatureFlags;
  /** Set when the event is on an enterprise portal, where everything is on. */
  readonly enterprise: { readonly organizationId: string; readonly name: string; readonly slug: string } | null;
}

/** An override write: true or false sets it, null clears it so the event follows the default. */
export type PortalFeatureOverrideWrites = Readonly<Record<string, boolean | null>>;

export const readPortalFeatureDefaults = (signal?: AbortSignal): Promise<PortalFeatureDefaults> =>
  getJson<PortalFeatureDefaults>('/portal-features', undefined, signal);

export const readEventPortalFeatures = (eventId: string, signal?: AbortSignal): Promise<EventPortalFeatures> =>
  getJson<EventPortalFeatures>(`/events/${eventId}/portal-features`, undefined, signal);

export const saveEventPortalFeatures = (eventId: string, features: PortalFeatureOverrideWrites): Promise<EventPortalFeatures> =>
  sendJson<EventPortalFeatures>('patch', `/events/${eventId}/portal-features`, { features });
